import React, {
  useCallback,
  useState,
  FC,
  useMemo,
  useRef,
  useContext,
} from 'react'
import {
  Badge,
  Button,
  List,
  Popover,
  Caption,
  Grid,
  Heading,
  Tooltip,
} from '@design-systems/canvas-ui'
import { useMutation } from '@apollo/client/react'
import Icon, { ActivityIcon } from '@design-systems/react-icons'
import _ from 'lodash'
import BackgroundActivityToasters from '../atoms/BackgroundActivityToasters'
import BackgroundNotifications from '../atoms/BackgroundNotifications'
import { UPDATE_BACKGROUND_NOTIFICATION } from 'graphql/mutations/backgroundActivity/backgroundActivityMutations'
import { getBackgroundActivityDropdownHeaderText } from '../utils/backgroundNotificationsHelper'
import { BackgroundActivityContext } from '../organisms/BackgroundActivityContext'

enum BACKGROUND_NOTIFICATIONS_STATUS {
  INPROGRESS = 'INPROGRESS',
  ERROR = 'ERROR',
  COMPLETED = 'COMPLETED',
}

const BackgroundActivity: FC = () => {
  const notificationRef = useRef([])

  const { allActivities, removeActivity, generatedTime, callSetGenerateTime } =
    useContext(BackgroundActivityContext)

  const [isPopoverActive, setIsPopoverActive] = useState(false)

  const backgroundActivities = useMemo(() => {
    return allActivities?.filter((activity) => !activity.archived)
  }, [allActivities])
  const inProgressNotifications = _.filter(backgroundActivities, {
    status: BACKGROUND_NOTIFICATIONS_STATUS.INPROGRESS,
  })
  const failedNotifications = _.filter(backgroundActivities, {
    status: BACKGROUND_NOTIFICATIONS_STATUS.ERROR,
  })
  const completedNotifications = _.filter(backgroundActivities, {
    status: BACKGROUND_NOTIFICATIONS_STATUS.COMPLETED,
  })
  const inProgressCount = inProgressNotifications.length
  const completedCount = completedNotifications.length
  const failedCount = failedNotifications.length
  const totalCount = inProgressCount + completedCount + failedCount
  const headerText = getBackgroundActivityDropdownHeaderText(
    inProgressCount,
    completedCount,
  )

  const [updateBackgroundNotification] = useMutation(
    UPDATE_BACKGROUND_NOTIFICATION,
  )

  const clearCompleted = useCallback(async () => {
    if (completedNotifications.length === 0) return
    await updateBackgroundNotification({
      variables: {
        notificationBulkUpdate: {
          notificationUpdates: completedNotifications.map((activity) => ({
            id: activity.id,
            archived: true,
            visited: true,
          })),
        },
      },
    })
    // Optimistically tombstone each completed notification; the overlay
    // entries are GC'd once the server stops returning them.
    completedNotifications.forEach((activity) => removeActivity(activity.id))
    callSetGenerateTime()
  }, [
    completedNotifications,
    removeActivity,
    callSetGenerateTime,
    updateBackgroundNotification,
  ])

  const togglePopover = () => setIsPopoverActive(!isPopoverActive)

  return (
    <>
      <Popover
        location="bottom-right"
        onRequestClose={togglePopover}
        size="lg"
        className=""
        type="clickOnly"
        isVisible={isPopoverActive}
        content={
          <div
            className={
              totalCount > 7 ? 'spark-BackgroundActivity__ScrollContainer ' : ''
            }
          >
            <div className="hc8-pa-2x hc8-mb-3x">
              <Grid.Container spacing="dense" justify="space-between">
                <Grid.Item>
                  <Heading size={2}>Background activity</Heading>
                </Grid.Item>
                <Grid.Item>
                  <p
                    className="hc8-ta-left"
                    data-test="backgroundActivityCount"
                  >
                    {headerText}
                  </p>
                </Grid.Item>
                <Grid.Item>
                  {completedCount > 0 && (
                    <Button
                      size="sm"
                      data-test="backgroundActivityClear"
                      onClick={clearCompleted}
                    >
                      Clear done ({completedCount})
                    </Button>
                  )}
                </Grid.Item>
              </Grid.Container>
            </div>
            {completedCount > 0 && (
              <div data-test="backgroundActivityDownloadList">
                <strong className="hc8-ml-2x">Done</strong>
                <List>
                  <BackgroundNotifications
                    notifications={completedNotifications}
                  />
                </List>
              </div>
            )}
            {(inProgressCount > 0 || failedCount > 0) && (
              <div data-test="backgroundActivityProgressList">
                <p className="hc8-fs-050 hc8-pt-2x hc8-pl-3x hc8-ta-left">
                  <strong>In progress...</strong>
                </p>
                <List>
                  <BackgroundNotifications
                    notifications={failedNotifications}
                  />
                  <BackgroundNotifications
                    notifications={inProgressNotifications}
                  />
                </List>
              </div>
            )}
            <div className="hc8-pa-2x">
              <Caption
                above="Items that are done are only stored in this view for 7 days."
                data-test="backgroundActivityFooter"
              />
            </div>
          </div>
        }
      >
        <Tooltip location="bottom-right" content="Background activity">
          <GenerateBadge count={inProgressCount}>
            <Button
              variant="ghost-neutral"
              aria-label="Show background activity"
              isIconOnly
              data-test="backgroundActivityDropdown"
              onClick={togglePopover}
            >
              <Icon svg={ActivityIcon} />
            </Button>
          </GenerateBadge>
        </Tooltip>
      </Popover>
      <div data-test="sparkBackgroundActivityMain">
        <BackgroundActivityToasters
          generatedTime={generatedTime}
          backgroundActivities={backgroundActivities}
          notificationRef={notificationRef}
        />
      </div>
    </>
  )
}

export const GenerateBadge = ({
  count,
  children,
}: {
  count: number
  children: React.ReactNode
}) => {
  return count > 0 ? (
    <Badge
      aria-label={`${count} background activity successes`}
      content={`${count}`}
      color="positive"
    >
      {children}
    </Badge>
  ) : (
    children
  )
}

export default BackgroundActivity
